import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Building2 } from "lucide-react";
import { useAuth, homeFor } from "@/lib/auth";
import { NAV_LINKS } from "@/lib/marketing";

function Wordmark({ size = "text-xl" }) {
  return (
    <Link to="/" className="flex items-center gap-2.5">
      <div className="h-8 w-8 rounded-xl bg-primary grid place-items-center shrink-0 shadow-sm shadow-primary/30">
        <Building2 className="h-4 w-4 text-white" />
      </div>
      <div className={`font-display ${size} font-extrabold tracking-tighter leading-none`}>
        Campus<span className="text-cobalt-500">Events</span>
        <span className="block text-[10px] font-mono font-normal tracking-widest uppercase text-muted-foreground">by Arventiq Labs</span>
      </div>
    </Link>
  );
}

export default function MarketingLayout() {
  const { toggleTheme, theme, isAuthed, user } = useAuth();
  const loc = useLocation();

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      {/* Ambient background */}
      <div className="orb bg-cobalt-500/50" style={{ width: 520, height: 520, top: -160, left: -180 }} />
      <div className="orb bg-cyanx/40" style={{ width: 460, height: 460, top: 60, right: -120 }} />
      <div className="orb bg-chartreuse/40" style={{ width: 380, height: 380, bottom: -160, left: "35%" }} />
      <div className="absolute inset-0 grid-lines pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 lg:px-14 py-5">
        <Wordmark />
        <div className="flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to}
              className={({ isActive }) =>
                `hidden sm:inline text-sm font-medium px-3 py-1.5 rounded-full transition-colors hover:text-cobalt-500 ${isActive ? "text-cobalt-500 bg-cobalt-500/10" : ""}`}>
              {l.label}
            </NavLink>
          ))}
          <button onClick={toggleTheme}
            className="glass rounded-full px-4 py-1.5 text-xs font-medium hover:-translate-y-0.5 transition-transform ml-2">
            {theme === "dark" ? "Light" : "Dark"}
          </button>
          {isAuthed ? (
            <Link to={homeFor(user)} className="text-sm font-medium hover:text-cobalt-500 ml-2 whitespace-nowrap">
              Continue as {user?.name} &rarr;
            </Link>
          ) : (
            <Link to="/login" className="text-sm font-medium hover:text-cobalt-500 inline-flex items-center gap-1 ml-2 whitespace-nowrap">
              Sign in <ArrowUpRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      </header>

      <main className="relative z-10">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={loc.pathname}
            initial={{ opacity: 0, x: 14 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-black/5 dark:border-white/10 py-10 px-6 lg:px-14">
        <div className="max-w-7xl mx-auto grid sm:grid-cols-[1.5fr_1fr_1fr] gap-8">
          <div>
            <Wordmark size="text-lg" />
            <p className="mt-3 text-sm text-muted-foreground max-w-xs">
              Auditorium booking and event management for BMS Institute of Technology &amp; Management &mdash; from request to QR check-in and certificates.
            </p>
            <a href="https://arventiqlabs.com" target="_blank" rel="noreferrer"
              className="mt-4 inline-flex flex-col gap-1 opacity-80 hover:opacity-100 transition-opacity">
              <span className="text-[9px] text-mono uppercase tracking-widest text-muted-foreground">Powered by</span>
              <img src="/brand/arventiq-full.png" alt="Arventiq Labs" className="h-6 w-auto dark:brightness-0 dark:invert" />
            </a>
          </div>
          <div>
            <div className="overline mb-3">Platform</div>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/features" className="hover:text-cobalt-500">Features</Link></li>
              <li><Link to="/how-it-works" className="hover:text-cobalt-500">Modules</Link></li>
              <li><Link to="/events" className="hover:text-cobalt-500">Public events</Link></li>
              <li><Link to="/login" className="hover:text-cobalt-500">Sign in</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline mb-3">Arventiq Labs</div>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><a href="https://arventiqlabs.com" target="_blank" rel="noreferrer" className="hover:text-cobalt-500">Home</a></li>
              <li><a href="https://arventiqlabs.com/services.html" target="_blank" rel="noreferrer" className="hover:text-cobalt-500">Services</a></li>
              <li><a href="https://fortifyhub.net" target="_blank" rel="noreferrer" className="hover:text-cobalt-500">FortifyHub</a></li>
              <li><Link to="/contact" className="hover:text-cobalt-500">Contact</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-8 pt-6 border-t border-black/5 dark:border-white/10 flex flex-wrap justify-between gap-2 text-xs text-muted-foreground">
          <div>&copy; 2026 Arventiq Labs &middot; Campus Events Platform &middot; Bengaluru, India</div>
          <div className="text-mono uppercase tracking-widest">Innovate &middot; Learn &middot; Secure</div>
        </div>
      </footer>
    </div>
  );
}
